import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Alert,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { fetchPackById, updatePackApi } from "../../services/apiServices";

const UpdatePack = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");
  const [pack, setPack] = useState(null);

  // Form states
  const [packAmount, setPackAmount] = useState("");
  const [image, setImage] = useState("");
  const [previewImage, setPreviewImage] = useState("");
  const [file, setFile] = useState(null);

  useEffect(() => {
    const getPack = async () => {
      try {
        setLoading(true);
        const response = await fetchPackById(id);

        if (response.status && response.data) {
          const packData = response.data;
          setPack(packData);
          setPackAmount(packData.packAmount);
          setPreviewImage(packData.wallpaper || "");
        } else {
          setError("Failed to fetch pack details");
        }
      } catch (err) {
        console.error(err);
        setError("Failed to fetch pack details");
      } finally {
        setLoading(false);
      }
    };

    getPack();
  }, [id]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);

      // Validate file type
      const validTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
        "image/gif",
      ];
      if (!validTypes.includes(selectedFile.type)) {
        toast.error("Please select a valid image file (JPEG, PNG, WEBP, GIF)");
        e.target.value = "";
        return;
      }

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (selectedFile.size > maxSize) {
        toast.error("File size should be less than 5MB");
        e.target.value = "";
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleImageUrlChange = (e) => {
    setImage(e.target.value);
    setPreviewImage(e.target.value);
    setFile(null); // Clear file if URL is entered
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!packAmount) {
      toast.error("Pack amount is required");
      return;
    }

    if (!image && !file) {
      toast.error("Please provide an image URL or upload a file");
      return;
    }

    try {
      setUpdating(true);

      // Create FormData object
      const formData = new FormData();

      // Append all required fields
      formData.append("packsId", id);
      formData.append("packAmount", packAmount);

      // Append image URL (if provided)
      if (image) {
        formData.append("image", image);
      }

      // Append file (if provided)
      if (file) {
        formData.append("file", file);
      }

      // Log FormData contents for debugging
      console.log("FormData contents:");
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }

      // Call update API
      const response = await updatePackApi(formData);

      if (response.status) {
        toast.success(response.message || "Pack updated successfully!");
        navigate(`/packs/view/${id}`);
      } else {
        toast.error(response.message || "Failed to update pack");
      }
    } catch (err) {
      console.error("Update error:", err);

      // Show specific error message if available
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error("An error occurred while updating the pack");
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleReset = () => {
    if (pack) {
      setPackAmount(pack.packAmount);
      setImage("");
      setPreviewImage(pack.wallpaper || "");
      setFile(null);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center p-5">
            <Spinner color="primary" />
            <p className="mt-2">Loading pack details...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">{error}</Alert>
          <Button color="secondary" onClick={() => navigate("/packs")}>
            Back to Packs
          </Button>
        </Container>
      </div>
    );
  }

  if (!pack) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="warning">Pack not found</Alert>
          <Button color="secondary" onClick={() => navigate("/packs")}>
            Back to Packs
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="PackDraw" breadcrumbItem="Update Pack" />

        <Row className="mb-3">
          <Col>
            <h4 className="mb-0">Update Pack: {pack.name}</h4>
            <p className="text-muted">ID: {pack._id}</p>
          </Col>
          <Col className="text-end">
            <Button
              color="secondary"
              onClick={() => navigate(`/packs/view/${id}`)}
              className="me-2">
              <i className="ri-arrow-left-line"></i> Back to Details
            </Button>
            <Button color="info" onClick={() => navigate("/packs")}>
              <i className="ri-list-check"></i> View All Packs
            </Button>
          </Col>
        </Row>

        <Row>
          <Col lg={8}>
            <Card className="shadow-sm border-0">
              <CardHeader className="bg-primary text-white">
                <h5 className="mb-0">Edit Pack Details</h5>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleSubmit} id="updatePackForm">
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="packName" className="fw-bold">
                          Pack Name
                        </Label>
                        <Input
                          type="text"
                          id="packName"
                          value={pack.name}
                          disabled
                          className="bg-light"
                          readOnly
                        />
                        <small className="text-muted">
                          Pack name cannot be changed
                        </small>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="packAmount" className="fw-bold">
                          Pack Amount *
                        </Label>
                        <Input
                          type="number"
                          id="packAmount"
                          value={packAmount}
                          onChange={(e) => setPackAmount(e.target.value)}
                          placeholder="Enter pack amount"
                          required
                          min="1"
                          step="1"
                          className="form-control-lg"
                        />
                        <small className="text-muted">
                          Current: {pack.packAmount}
                        </small>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="imageUrl" className="fw-bold">
                          Image URL
                        </Label>
                        <Input
                          type="url"
                          id="imageUrl"
                          value={image}
                          onChange={handleImageUrlChange}
                          placeholder="Enter image URL"
                          className="form-control-lg"
                        />
                        <small className="text-muted">
                          Leave empty if uploading file
                        </small>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="fileUpload" className="fw-bold">
                          Upload Image File
                        </Label>
                        <Input
                          type="file"
                          id="fileUpload"
                          onChange={handleFileChange}
                          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                          className="form-control-lg"
                        />
                        <small className="text-muted">
                          Max 5MB. JPEG, PNG, WEBP, GIF
                        </small>
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Image Preview Section */}
                  {previewImage && (
                    <FormGroup>
                      <Label className="fw-bold d-block mb-2">
                        Image Preview
                      </Label>
                      <div className="border rounded p-3 text-center bg-light">
                        <img
                          src={previewImage}
                          alt="Preview"
                          className="img-fluid rounded mb-2"
                          style={{
                            maxHeight: "200px",
                            maxWidth: "100%",
                            objectFit: "contain",
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src =
                              "https://via.placeholder.com/300x200?text=Image+Not+Found";
                          }}
                        />
                        <div>
                          <small
                            className={`badge ${file ? "bg-info" : "bg-secondary"}`}>
                            {file ? "New Upload" : "Current Image"}
                          </small>
                        </div>
                      </div>
                    </FormGroup>
                  )}

                  {/* Current Image Info */}
                  {pack.wallpaper && !file && (
                    <Alert color="info" className="mt-3">
                      <div className="d-flex align-items-center">
                        <i className="ri-information-line me-2"></i>
                        <div>
                          <strong>Current Image:</strong>{" "}
                          {pack.wallpaper.substring(0, 50)}...
                          <br />
                          <small>
                            Upload a new file or enter a new URL to change
                          </small>
                        </div>
                      </div>
                    </Alert>
                  )}

                  <div className="mt-4 pt-3 border-top">
                    <Button
                      type="submit"
                      color="primary"
                      className="me-2 px-4 py-2"
                      disabled={updating}>
                      {updating ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <i className="ri-save-line me-2"></i>
                          Update Pack
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      color="secondary"
                      onClick={handleReset}
                      className="me-2">
                      <i className="ri-refresh-line me-2"></i>
                      Reset Changes
                    </Button>
                    <Button
                      type="button"
                      color="light"
                      onClick={() => navigate(`/packs/view/${id}`)}>
                      Cancel
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>

          {/* Sidebar Information */}
          <Col lg={4}>
            <Card className="shadow-sm border-0 mb-4">
              <CardHeader className="bg-warning text-white">
                <h5 className="mb-0">
                  <i className="ri-lightbulb-line me-2"></i>
                  Update Instructions
                </h5>
              </CardHeader>
              <CardBody>
                <div className="alert alert-light mb-3">
                  <h6 className="alert-heading mb-2">Important Notes:</h6>
                  <ul className="mb-0 ps-3">
                    <li>Pack Name cannot be modified</li>
                    <li>Pack Amount is required</li>
                    <li>Provide either URL or File (not both)</li>
                    <li>File size limit: 5MB</li>
                  </ul>
                </div>

                <div className="alert alert-info mb-0">
                  <h6 className="alert-heading mb-2">API Information:</h6>
                  <div className="small">
                    <strong>Endpoint:</strong> /api/v2/admin/auth/packs/update
                    <br />
                    <strong>Method:</strong> PUT
                    <br />
                    <strong>Content-Type:</strong> multipart/form-data
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card className="shadow-sm border-0">
              <CardHeader className="bg-success text-white">
                <h5 className="mb-0">
                  <i className="ri-information-line me-2"></i>
                  Pack Statistics
                </h5>
              </CardHeader>
              <CardBody>
                <div className="d-flex flex-column gap-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Total Items:</span>
                    <Badge color="info" pill className="fs-6">
                      {pack.items?.length || 0}
                    </Badge>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Unique Items:</span>
                    <Badge color="primary" pill className="fs-6">
                      {new Set(pack.items?.map((item) => item.name)).size || 0}
                    </Badge>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Total Value:</span>
                    <span className="fw-bold text-success">
                      $
                      {pack.items?.reduce(
                        (sum, item) => sum + (item.amount || 0),
                        0,
                      ) || 0}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Outcome:</span>
                    <Badge
                      color={pack.outCome >= 1 ? "success" : "warning"}
                      pill
                      className="fs-6">
                      {pack.outCome}x
                    </Badge>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Status:</span>
                    <Badge
                      color={pack.isApproved ? "success" : "danger"}
                      pill
                      className="fs-6">
                      {pack.isApproved ? "Approved" : "Pending"}
                    </Badge>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UpdatePack;
